import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';

import * as usersActions from '../../store/users/actions';
import * as authActions from '../../store/auth/actions';
import * as acquisitionOrdersActions from '../../store/acquisitionOrders/actions';
import { useDispatch, useSelector } from 'react-redux';
import { hasRole } from '../../selectors/auth';
import roles from '../../config/roles';
import Modal from '../../components/Common/Modal/Modal';
import ShowSingleElement from '../../components/Common/ShowSingleElement';
import FormUser from '../../components/Users/FormUser';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/Users/tableColumns';
import BatchUserBar from '../../components/Users/BatchUserBar';
import {
  CountRowTablePlacement,
  ExportButtonPlacement,
  ToggleColumnButton,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';

const BATCH_EDIT_ROLES = [
  roles.SUPER_ADMIN,
  roles.ADMIN,
  roles.MANAGER,
  roles.TEAM_LEADER,
];

const List = (props) => {
  const loading = useSelector((state) => state.User.loading);
  const loadingAcquisitionOrders = useSelector(
    (state) => state.AcquisitionOrder.loading,
  );
  const users = useSelector((state) => state.User.users);
  const acquisitionOrders = useSelector(
    (state) => state.AcquisitionOrder.acquisitionOrders,
  );
  const user = useSelector((state) => state.Auth.admin);
  const isImpersonating = useSelector((state) => state.Auth.isImpersonating);
  const canBatchEdit = useSelector(hasRole(BATCH_EDIT_ROLES));
  const companyId = useSelector((state) => state.Dashboard.companyId);

  const [viewModal, setViewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(usersActions.syncUsers());
    dispatch(acquisitionOrdersActions.syncAcquisitionOrders());
  }, []);

  useEffect(() => {
    loading && setSelectedUserIds([]);
  }, [loading]);

  const openEditModal = (user = null) => {
    setSelectedUser(user);
    setEditModal(true);
  };

  const openViewModal = (user) => {
    setSelectedUser(user);
    setViewModal(true);
  };

  const handleImpersonateUser = (targetUser) => {
    dispatch(authActions.startImpersonation(targetUser.id));
  };

  const columns = useMemo(
    () =>
      createTableColumns(
        openViewModal,
        openEditModal,
        handleImpersonateUser,
        acquisitionOrders,
        user,
        companyId,
        isImpersonating,
      ),
    [acquisitionOrders, user, companyId, isImpersonating],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  const editableUsers = useMemo(
    () => users.filter(({ id, role }) => role !== user.role || id === user.id),
    [users, user],
  );
  const nonEditableUserIds = useMemo(
    () =>
      users
        .filter(({ id, role }) => role === user.role && id !== user.id)
        .map(({ id }) => id),
    [users, user],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Users" />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end m-auto">
                    <CountRowTablePlacement />
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={users}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    selection={canBatchEdit ? selectedUserIds : undefined}
                    onSelectionChange={
                      canBatchEdit ? setSelectedUserIds : undefined
                    }
                    nonSelectableIds={nonEditableUserIds}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            size="lg"
            title="User"
            isOpen={viewModal}
            toggle={() => setViewModal(!viewModal)}
            showClose
            scrollable={false}
          >
            <ShowSingleElement collection="users" element={selectedUser} />
          </Modal>
          <Modal
            size="xl"
            title="User"
            isOpen={editModal}
            toggle={() => setEditModal(!editModal)}
            scrollable={false}
          >
            <FormUser
              closeModal={() => setEditModal(false)}
              create={!selectedUser}
              loading={loading || loadingAcquisitionOrders}
              acquisitionOrders={acquisitionOrders}
              user={selectedUser}
            />
          </Modal>
          {canBatchEdit && (
            <BatchUserBar
              users={editableUsers}
              selectedUserIds={selectedUserIds}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default List;
